import React, { useRef, useState, useEffect } from 'react';
import {
  X,
  ArrowLeft,
  Plus,
  Minus,
  Trash2,
  ShoppingCart,
} from 'lucide-react';
import emailjs from '@emailjs/browser';

const OrderModal = ({
  isOpen,
  onClose,
  selectedProduct,
  cartItems,
  updateCartQuantity,
  removeFromCart,
  clearCart,
}) => {
  const formRef = useRef(null);
  const modalRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    product: selectedProduct || '',
    quantity: '',
    address: '',
    instructions: '',
  });

  // Update selected product
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      product: selectedProduct || '',
    }));
  }, [selectedProduct]);

  // ESC key + prevent background scrolling
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Close when clicking outside
  const handleOverlayClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  // Normal inputs
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Phone number
  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);

    setFormData({
      ...formData,
      phone: value,
    });
  };

  // --------------------------------
  // PRICE CALCULATION
  // --------------------------------

  const getItemSubtotal = (item) => {
    return Number(item.rate) * Number(item.quantity);
  };

  const cartTotal = (cartItems || []).reduce(
    (total, item) => total + getItemSubtotal(item),
    0
  );

  // --------------------------------
  // EMAIL CART DETAILS
  // --------------------------------

  const getCartDetails = () => {
    if (!cartItems || cartItems.length === 0) {
      return `${formData.product} | Quantity: ${formData.quantity}`;
    }

    return cartItems
      .map(
        (item) =>
          `${item.name} | ${item.sizes.join(
            ' / '
          )} | ₹${item.rate} × ${item.quantity} = ₹${getItemSubtotal(item)}`
      )
      .join('\n');
  };

  // --------------------------------
  // SUBMIT ORDER
  // --------------------------------

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate phone
    if (!/^[6-9][0-9]{9}$/.test(formData.phone)) {
      alert('Please enter a valid 10-digit Indian mobile number.');
      return;
    }

    const hasCart = cartItems && cartItems.length > 0;

    const cartDetails = getCartDetails();

    const productValue = hasCart
      ? cartItems.map((item) => item.name).join(', ')
      : formData.product;

    const quantityValue = hasCart
      ? cartItems
          .map((item) => `${item.name}: ${item.quantity}`)
          .join(', ')
      : formData.quantity;

    // Add total to the email
    const totalValue = hasCart
      ? `₹${cartTotal}`
      : 'Single product order';

    // Hidden EmailJS fields
    const hiddenProduct = document.createElement('input');
    hiddenProduct.type = 'hidden';
    hiddenProduct.name = 'product';
    hiddenProduct.value = productValue;

    const hiddenQuantity = document.createElement('input');
    hiddenQuantity.type = 'hidden';
    hiddenQuantity.name = 'quantity';
    hiddenQuantity.value = quantityValue;

    const hiddenCartDetails = document.createElement('input');
    hiddenCartDetails.type = 'hidden';
    hiddenCartDetails.name = 'cart_details';
    hiddenCartDetails.value = cartDetails;

    const hiddenTotal = document.createElement('input');
    hiddenTotal.type = 'hidden';
    hiddenTotal.name = 'total';
    hiddenTotal.value = totalValue;

    formRef.current.appendChild(hiddenProduct);
    formRef.current.appendChild(hiddenQuantity);
    formRef.current.appendChild(hiddenCartDetails);
    formRef.current.appendChild(hiddenTotal);

    emailjs
      .sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        formRef.current,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      )
      .then(() => {
        alert("Order placed successfully! We'll contact you soon.");

        hiddenProduct.remove();
        hiddenQuantity.remove();
        hiddenCartDetails.remove();
        hiddenTotal.remove();

        setFormData({
          name: '',
          phone: '',
          product: '',
          quantity: '',
          address: '',
          instructions: '',
        });

        if (hasCart) {
          clearCart();
        }

        onClose();
      })
      .catch((error) => {
        console.error('EmailJS error:', error);

        hiddenProduct.remove();
        hiddenQuantity.remove();
        hiddenCartDetails.remove();
        hiddenTotal.remove();

        alert(
          'Failed to send order. Please try again or call us directly.'
        );
      });
  };

  if (!isOpen) return null;

  const hasCart = cartItems && cartItems.length > 0;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={handleOverlayClick}
    >
      <div
        ref={modalRef}
        className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl"
      >

        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft size={20} />
            </button>

            <h2 className="text-2xl font-semibold text-gray-800">
              {hasCart ? 'Your Cart' : 'Place Your Order'}
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="p-6 space-y-4"
        >

          {/* CART */}
          {hasCart && (
            <div className="space-y-3">

              <div className="flex items-center gap-2 text-gray-800 font-semibold">
                <ShoppingCart size={20} />
                <span>Selected Products</span>
              </div>

              {cartItems.map((item) => {
                const subtotal = getItemSubtotal(item);

                return (
                  <div
                    key={item.name}
                    className="border border-gray-200 rounded-lg p-3"
                  >
                    <div className="flex justify-between gap-3">

                      <div className="flex-1">
                        <h3 className="font-medium text-gray-800">
                          {item.name}
                        </h3>

                        <p className="text-sm text-gray-500">
                          {item.sizes.join(' / ')}
                        </p>

                        <p className="text-sm text-green-700 font-medium mt-1">
                          ₹{item.rate} × {item.quantity}
                        </p>

                        {/* ITEM SUBTOTAL */}
                        <p className="text-sm font-semibold text-gray-800 mt-1">
                          Subtotal: ₹{subtotal}
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => removeFromCart(item.name)}
                        className="text-red-500 hover:text-red-700"
                        aria-label={`Remove ${item.name}`}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>

                    {/* Quantity */}
                    <div className="flex items-center justify-between mt-3">

                      <span className="text-sm text-gray-600">
                        Quantity
                      </span>

                      <div className="flex items-center gap-3">

                        <button
                          type="button"
                          onClick={() =>
                            updateCartQuantity(
                              item.name,
                              item.quantity - 1
                            )
                          }
                          disabled={item.quantity <= 1}
                          className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-40 flex items-center justify-center"
                        >
                          <Minus size={16} />
                        </button>

                        <span className="font-semibold min-w-[20px] text-center">
                          {item.quantity}
                        </span>

                        <button
                          type="button"
                          onClick={() =>
                            updateCartQuantity(
                              item.name,
                              item.quantity + 1
                            )
                          }
                          className="w-8 h-8 rounded-full bg-blue-600 text-white hover:bg-blue-700 flex items-center justify-center"
                        >
                          <Plus size={16} />
                        </button>

                      </div>
                    </div>
                  </div>
                );
              })}

              {/* TOTAL */}
              <div className="border-t pt-4 mt-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-800">
                    Total
                  </span>

                  <span className="text-xl font-bold text-green-700">
                    ₹{cartTotal}
                  </span>
                </div>
              </div>

              {/* Clear cart */}
              <button
                type="button"
                onClick={clearCart}
                className="text-sm text-red-500 hover:text-red-700"
              >
                Clear Cart
              </button>
            </div>
          )}

          {/* NAME */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name *
            </label>

            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* PHONE */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number *
            </label>

            <div className="flex">
              <span className="flex items-center px-3 bg-gray-100 border border-gray-300 border-r-0 rounded-l-lg text-gray-600">
                +91
              </span>

              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handlePhoneChange}
                placeholder="Enter 10-digit number"
                required
                pattern="[6-9][0-9]{9}"
                maxLength="10"
                inputMode="numeric"
                className="w-full border border-gray-300 rounded-r-lg p-2 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <p className="text-xs text-gray-500 mt-1">
              Enter a valid 10-digit mobile number
            </p>
          </div>

          {/* SINGLE PRODUCT MODE */}
          {!hasCart && (
            <>
              {/* PRODUCT */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product *
                </label>

                {selectedProduct ? (
                  <>
                    <div className="w-full border border-gray-300 rounded-lg p-2 bg-gray-100 text-gray-700">
                      {selectedProduct}
                    </div>

                    <input
                      type="hidden"
                      name="product"
                      value={selectedProduct}
                    />
                  </>
                ) : (
                  <select
                    name="product"
                    value={formData.product}
                    onChange={handleChange}
                    required
                    className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a product</option>
                    <option>Full Cream Milk</option>
                    <option>Toned Milk</option>
                    <option>Cow Milk</option>
                    <option>Buffalo Milk Curd</option>
                    <option>Butter</option>
                    <option>Pure Cow Ghee</option>
                    <option>Pure Buffalo Ghee</option>
                    <option>Jaggery Powder</option>
                    <option>Jaggery Blocks</option>
                    <option>Cashew</option>
                  </select>
                )}
              </div>

              {/* QUANTITY */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantity *
                </label>

                <input
                  type="text"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  placeholder="e.g., 2 x 500ml, 1 x 1L"
                  required
                  className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </>
          )}

          {/* ADDRESS */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Delivery Address *
            </label>

            <textarea
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
              rows="3"
              className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* INSTRUCTIONS */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Special Instructions
            </label>

            <textarea
              name="instructions"
              value={formData.instructions}
              onChange={handleChange}
              rows="2"
              className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* BUTTONS */}
          <div className="flex gap-3 pt-2">

            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 transition font-semibold"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={hasCart && cartItems.length === 0}
              className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition font-semibold disabled:bg-gray-400"
            >
              Submit Order
            </button>

          </div>
        </form>
      </div>
    </div>
  );
};

export default OrderModal;